/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { FiRotateCcw, FiTrash2 } from 'react-icons/fi'

import { isBoundaryTruncated, readBoundarySnapshot } from './geofenceBoundary'
import { GeofenceDiffMap, type LatLng, type MovedPoint } from './GeofenceDiffMap'
import type { RendererProps } from './types'

import type { AuditDiffEntry } from '../../types'
import { stateOf, type NormalizedChange } from '../diff/normalize'
import { ChangeRow, Section } from '../primitives'

/**
 * Geofence renderer — handles create, update and delete.
 *
 * A geofence edit is dominated by geometry and bookkeeping — a wall of point coordinates, per-point
 * ids that change on every save, a version counter and a derived bounding box. None of that is worth
 * reading as rows. So this renderer keeps only what a person changed on purpose:
 *
 *  - the **boundary**, shown as a before/after map rather than coordinates (see `GeofenceDiffMap`);
 *    but only when the record carries a whole-field snapshot of it — see `geofenceBoundary.ts`. A
 *    polygon assembled from the diff would be a shape nobody drew, so older records get their moved
 *    vertices plotted as loose markers instead, and no outline;
 *  - **categories**, reduced to their names;
 *  - **name**, **display layout** (z-order), **visibility** and any other real metadata;
 *  - the resulting **version**, shown as the new number rather than a before → after change.
 *
 * A delete is a flag flip saved as an `update`, so it is lifted out of the rows into a banner —
 * see `lifecycleOf` below.
 *
 * Everything else — `geofencepoints.*`, `geofencecategories.*.id`, the `*LongitudeDeg` / `*LatitudeDeg`
 * bounding box and the `version` bump itself — is deliberately dropped from the rows. The raw diff is
 * always one disclosure away for support.
 */

// Roots and fields that are noise: internal ids, the point array (shown on the map instead), the
// version counter (surfaced separately) and the server-derived bounding box.
const BOUNDING_BOX = /^(west|east|north|south)most(Longitude|Latitude)Deg$/

/** Fields surfaced with bespoke rendering that should not also appear in the generic Details section. */
const BESPOKE_FIELDS = new Set(['geofencepoints', 'geofencecategories', 'version', 'visible'])

const isHiddenMeta = (change: NormalizedChange): boolean => {
  const root = change.segments[0] ?? ''
  return BESPOKE_FIELDS.has(root) || BOUNDING_BOX.test(root)
}

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined)

// ---- Visibility row --------------------------------------------------------

/**
 * A specialised change row for the `visible` field that shows "Visible" / "Hidden" instead of the
 * generic "Enabled" / "Disabled" that `formatValue` produces for booleans.
 */
const visibilityLabel = (value: unknown): string => {
  if (value === true) return 'Visible'
  if (value === false) return 'Hidden'
  return '—'
}

const VisibilityRow: React.FC<{ before?: unknown; after?: unknown; state: NormalizedChange['state'] }> = ({
  before,
  after,
  state,
}) => {
  const fakeChange: NormalizedChange = {
    key: 'visible',
    state,
    label: 'Visibility',
    path: 'visible',
    segments: ['visible'],
    before: visibilityLabel(before),
    after: visibilityLabel(after),
  }
  return <ChangeRow change={fakeChange} />
}

// ---- Boundary reconstruction ----------------------------------------------

interface PartialPoint {
  lat?: number
  lng?: number
}

/** A vertex the edit touched, as far as the record describes it — one axis or both. */
interface VertexChange {
  index: number
  before: PartialPoint
  after: PartialPoint
}

interface Boundary {
  changed: boolean
  /** Every vertex the diff mentions coordinates for, whether or not it can be placed on a map. */
  vertices: VertexChange[]
  /** The subset of `vertices` with a full lat/lng on both sides, so a marker can be drawn. */
  moved: MovedPoint[]
  /** How many points the boundary has in total — every point is listed, even if only its id changed. */
  total: number
}

/**
 * Collect the vertices the point-level diff names, for records with no boundary snapshot.
 *
 * The diff restates only the points an edit touched — drag one corner of an eight-point fence and it
 * carries one pair of coordinates and eight re-minted ids. The untouched vertices are unrecoverable,
 * so this deliberately does not return anything shaped like a boundary: `moved` is a set of markers,
 * and the outline comes from the snapshot or not at all.
 */
const reconstructBoundary = (diff: AuditDiffEntry[]): Boundary => {
  const before = new Map<number, PartialPoint>()
  const after = new Map<number, PartialPoint>()
  const indices = new Set<number>()
  let changed = false

  const set = (bucket: Map<number, PartialPoint>, index: number, key: 'lat' | 'lng', value: unknown): void => {
    const num = asNumber(value)
    if (num === undefined) return
    const point = bucket.get(index) ?? {}
    point[key] = num
    bucket.set(index, point)
  }

  for (const entry of diff) {
    if (typeof entry.p !== 'string' || !entry.p.startsWith('geofencepoints')) continue

    const segments = entry.p.split('.')
    const index = Number(segments[1])
    if (!Number.isInteger(index)) continue
    // Every point is listed on a save, even when only its id was re-minted, so this is the true
    // size of the boundary — the denominator for "2 of 8 points moved".
    indices.add(index)

    if (segments.length === 2) {
      const object = entry.v as Record<string, unknown> | undefined
      if (!object || typeof object !== 'object') continue
      const lat = asNumber(object.latitude)
      const lng = asNumber(object.longitude)
      if (lat === undefined || lng === undefined) continue
      const bucket = stateOf(entry.o) === 'REMOVED' ? before : after
      bucket.set(index, { lat, lng })
      changed = true
      continue
    }

    const field = segments[2]
    if (field === 'latitude') {
      set(before, index, 'lat', entry.v)
      set(after, index, 'lat', entry.r)
      changed = true
    } else if (field === 'longitude') {
      set(before, index, 'lng', entry.v)
      set(after, index, 'lng', entry.r)
      changed = true
    }
    // `geofencepoints.*.id` is intentionally ignored: ids are re-minted on every save.
  }

  const complete = (point: PartialPoint | undefined): LatLng | undefined =>
    point && typeof point.lat === 'number' && typeof point.lng === 'number' ? (point as LatLng) : undefined

  const vertices: VertexChange[] = [...new Set([...before.keys(), ...after.keys()])]
    .sort((a, b) => a - b)
    .map((index) => ({ index, before: before.get(index) ?? {}, after: after.get(index) ?? {} }))

  // Dragging a corner straight east changes only its longitude, so the record carries one number and
  // the vertex cannot be placed on a map at all. Those still belong in the list below the map.
  const moved: MovedPoint[] = vertices
    .map((vertex) => ({ index: vertex.index, before: complete(vertex.before), after: complete(vertex.after) }))
    .filter((point) => point.before ?? point.after)

  return { changed, vertices, moved, total: indices.size }
}

/** "1 of 8 boundary points moved." — the denominator is what makes a partial edit readable. */
const movedSummary = ({ vertices, total }: Boundary): string => {
  const count = vertices.length
  const noun = count === 1 ? 'point' : 'points'
  return total > count ? `${count} of ${total} boundary points moved.` : `${count} boundary ${noun} moved.`
}

// ---- Vertex list -----------------------------------------------------------

/** Coordinates read as numbers, not prose: six decimals is the precision the source data carries. */
const coord = (value: number | undefined): string => (value === undefined ? '—' : value.toFixed(6))

/**
 * The coordinate changes for one vertex, axis by axis.
 *
 * An axis the record does not mention was not changed, so it is left out rather than shown as
 * unknown — the reader should not have to wonder whether we lost something.
 */
const VertexRow: React.FC<{ vertex: VertexChange }> = ({ vertex }) => {
  const axes: { label: string; before?: number; after?: number }[] = [
    { label: 'Latitude', before: vertex.before.lat, after: vertex.after.lat },
    { label: 'Longitude', before: vertex.before.lng, after: vertex.after.lng },
  ].filter((axis) => axis.before !== undefined || axis.after !== undefined)

  return (
    <div className="rounded-box border border-base-300 p-3">
      <p className="text-sm font-medium">Point {vertex.index + 1}</p>
      <dl className="mt-1.5 flex flex-col gap-1">
        {axes.map((axis) => (
          <div key={axis.label} className="flex flex-wrap items-center gap-2 text-sm">
            <dt className="w-20 shrink-0 text-base-content/60">{axis.label}</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <span className="rounded-field border border-error/40 bg-error/10 px-2 py-0.5 tabular-nums text-error">
                {coord(axis.before)}
              </span>
              <span aria-hidden className="text-base-content/50">
                →
              </span>
              <span className="rounded-field border border-success/40 bg-success/10 px-2 py-0.5 tabular-nums text-success">
                {coord(axis.after)}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

// ---- Category changes ------------------------------------------------------

interface CategoryDiff {
  removed: string[]
  added: string[]
}

/**
 * Collect all category name changes and express them as two flat lists — removed and added.
 *
 * There is no "modified" concept for categories: a rename looks like the old name going away and a
 * new name arriving. A whole-object replace (o=1 on `categoryName`) is split into removed + added.
 */
const collectCategoryChanges = (diff: AuditDiffEntry[]): CategoryDiff => {
  const removed: string[] = []
  const added: string[] = []

  for (const entry of diff) {
    if (typeof entry.p !== 'string' || !entry.p.startsWith('geofencecategories')) continue
    const segments = entry.p.split('.')

    if (segments.length === 2) {
      const object = entry.v as Record<string, unknown> | undefined
      const name = object && typeof object === 'object' ? object.categoryName : undefined
      if (typeof name !== 'string') continue
      if (stateOf(entry.o) === 'REMOVED') removed.push(name)
      else added.push(name)
      continue
    }

    if (segments.length === 3 && segments[2] === 'categoryName') {
      if (typeof entry.v === 'string' && entry.v) removed.push(entry.v)
      if (typeof entry.r === 'string' && entry.r) added.push(entry.r)
    }
  }

  return { removed, added }
}

/** A compact pill chip for a category name. */
const CategoryChip: React.FC<{ name: string; tone: 'removed' | 'added' }> = ({ name, tone }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-field border px-2 py-1 text-sm ${tone === 'removed' ? 'border-error/40 bg-error/10 text-error' : 'border-success/40 bg-success/10 text-success'}`}
  >
    <span className="sr-only">{tone === 'removed' ? 'Removed:' : 'Added:'}</span>
    {name}
  </span>
)

/** One consolidated removed box and one consolidated added box for all category changes. */
const CategoryChanges: React.FC<{ diff: CategoryDiff }> = ({ diff: { removed, added } }) => {
  if (removed.length === 0 && added.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {removed.length > 0 && (
        <div className="rounded-box border border-base-300 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">Removed</p>
          <div className="flex flex-wrap gap-2">
            {removed.map((name) => (
              <CategoryChip key={name} name={name} tone="removed" />
            ))}
          </div>
        </div>
      )}
      {added.length > 0 && (
        <div className="rounded-box border border-base-300 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">Added</p>
          <div className="flex flex-wrap gap-2">
            {added.map((name) => (
              <CategoryChip key={name} name={name} tone="added" />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Soft delete / restore -------------------------------------------------

/**
 * A geofence is never hard-deleted: the application flips a `deleted` flag and saves, so the record
 * arrives as a `changeType: 'update'` whose only meaningful entry is `deleted: false → true`. Read as
 * generic rows that says "Deleted: Disabled → Enabled" buried under a version bump and a wall of
 * re-minted point ids — which is the opposite of what happened being obvious.
 *
 * So the flag is lifted out of the rows and stated plainly at the top of the panel. The same applies
 * in reverse when a geofence is brought back.
 */
type Lifecycle = 'deleted' | 'restored'

const lifecycleOf = (changes: NormalizedChange[]): Lifecycle | undefined => {
  const flag = changes.find((change) => change.path === 'deleted')
  if (!flag) return undefined
  if (flag.after === true && flag.before !== true) return 'deleted'
  if (flag.after === false && flag.before === true) return 'restored'
  return undefined
}

const LifecycleBanner: React.FC<{ lifecycle: Lifecycle; name: string }> = ({ lifecycle, name }) => {
  const deleted = lifecycle === 'deleted'
  const note = deleted ? 'Removed from the map.' : 'Brought back and visible again on the map.'

  return (
    <div
      className={`flex items-start gap-3 rounded-box border p-3 ${deleted ? 'border-error/40 bg-error/10' : 'border-success/40 bg-success/10'}`}
    >
      <span aria-hidden className={`mt-0.5 text-lg ${deleted ? 'text-error' : 'text-success'}`}>
        {deleted ? <FiTrash2 /> : <FiRotateCcw />}
      </span>
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${deleted ? 'text-error' : 'text-success'}`}>
          {deleted ? 'Geofence deleted' : 'Geofence restored'}
        </p>
        {name && (
          <p className="mt-0.5 truncate text-sm font-medium" title={name}>
            {name}
          </p>
        )}
        <p className="mt-1 text-xs text-base-content/60">{note}</p>
      </div>
    </div>
  )
}

// ---- Create view -----------------------------------------------------------

interface GeofenceEntity {
  geofenceName?: string
  version?: number
  zorder?: number
  visible?: boolean
  // Either an array or an object keyed by id — see `membersOf`.
  geofencepoints?: unknown
  geofencecategories?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * The members of a geofence collection, whichever shape the record stores it in.
 *
 * refresh writes categories keyed by id — `{ "362": { id: "362", categoryName: "Low Fuel" } }` —
 * where older records hold an array. Calling `.map` on the keyed shape threw and took the whole
 * page down to the error screen, so both shapes are read, and anything else is read as empty.
 */
const membersOf = (value: unknown): Record<string, unknown>[] =>
  (Array.isArray(value) ? (value as unknown[]) : isRecord(value) ? Object.values(value) : []).filter(isRecord)

/**
 * The "create" and "delete" paths: the diff is a single whole-object add (o=0, p="") or removal
 * (o=2, p=""). Extract the fields we care about and render them as a structured card rather than a
 * before/after diff — for a delete, the geofence as it stood when it was removed.
 */
const GeofenceSnapshotView: React.FC<{ entity: GeofenceEntity; mode: 'created' | 'deleted' }> = ({ entity, mode }) => {
  // `pointIndex` is the boundary's own ordering; a point without one keeps its position.
  const points: LatLng[] = membersOf(entity.geofencepoints)
    .map((p, position) => ({ order: asNumber(p.pointIndex) ?? position, lat: p.latitude, lng: p.longitude }))
    .sort((a, b) => a.order - b.order)
    .map(({ lat, lng }) => ({ lat, lng }))
    .filter((p): p is LatLng => typeof p.lat === 'number' && typeof p.lng === 'number')

  const categories = membersOf(entity.geofencecategories)
    .map((c) => c.categoryName)
    .filter((n): n is string => typeof n === 'string')

  const canShowMap = points.length >= 3

  return (
    <div className="flex flex-col gap-3">
      {canShowMap && (
        <Section title="Boundary" defaultOpen>
          {mode === 'created' ? (
            <GeofenceDiffMap before={[]} after={points} />
          ) : (
            <GeofenceDiffMap before={points} after={[]} beforeLabel="Values when deleted" />
          )}
        </Section>
      )}

      <div className="rounded-box border border-base-300 p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">
          {mode === 'created' ? 'Created with these values' : 'Values when deleted'}
        </p>
        <dl className="divide-y divide-base-200">
          {entity.geofenceName && (
            <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5">
              <dt className="text-sm text-base-content/60">Name</dt>
              <dd className="text-sm font-medium">{entity.geofenceName}</dd>
            </div>
          )}
          {categories.length > 0 && (
            <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5">
              <dt className="text-sm text-base-content/60">{categories.length === 1 ? 'Category' : 'Categories'}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {categories.map((name) => (
                  <span key={name} className="rounded-field border border-base-300 bg-base-200 px-2 py-0.5 text-sm">
                    {name}
                  </span>
                ))}
              </dd>
            </div>
          )}
          {entity.zorder !== undefined && (
            <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5">
              <dt className="text-sm text-base-content/60">Display layout</dt>
              <dd className="text-sm">{entity.zorder}</dd>
            </div>
          )}
          {entity.visible !== undefined && (
            <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5">
              <dt className="text-sm text-base-content/60">Visibility</dt>
              <dd className="text-sm">{entity.visible ? 'Visible' : 'Hidden'}</dd>
            </div>
          )}
          {entity.version !== undefined && (
            <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5">
              <dt className="text-sm text-base-content/60">Version</dt>
              <dd className="text-sm">{entity.version}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  )
}

// ---- Renderer --------------------------------------------------------------

/** The whole geofence carried by a create or delete record, if the diff is that shape. */
const wholeEntity = (diff: unknown): GeofenceEntity | undefined => {
  const entry = Array.isArray(diff) ? (diff[0] as AuditDiffEntry | undefined) : undefined
  return entry && !entry.p && entry.v && typeof entry.v === 'object' ? (entry.v as GeofenceEntity) : undefined
}

export const GeofenceRenderer: React.FC<RendererProps> = ({ audit, changes }) => {
  // Create: the diff is a single whole-object add; extract the entity and hand off to the create view.
  if (audit.changeType === 'create') {
    return <GeofenceSnapshotView entity={wholeEntity(audit.diff) ?? {}} mode="created" />
  }

  // Delete: refresh now records a deletion as its own change type, carrying the whole geofence as
  // it stood. Older deletions arrive as an update flipping `deleted`, handled by the banner below.
  const deletedEntity = audit.changeType === 'delete' ? wholeEntity(audit.diff) : undefined
  if (deletedEntity) {
    return (
      <div className="flex flex-col gap-3">
        <LifecycleBanner lifecycle="deleted" name={audit.entityName || (deletedEntity.geofenceName ?? '')} />
        <GeofenceSnapshotView entity={deletedEntity} mode="deleted" />
      </div>
    )
  }

  const boundary = React.useMemo(() => reconstructBoundary(audit.diff), [audit.diff])
  const categoryDiff = React.useMemo(() => collectCategoryChanges(audit.diff), [audit.diff])

  // The only source a polygon is ever drawn from. Absent on records written before the audit store
  // kept whole fields, which is why the fallback below exists at all.
  const snapshot = React.useMemo(() => readBoundarySnapshot(audit.metadata), [audit.metadata])
  const truncated = isBoundaryTruncated(audit.metadata)

  const visibilityChange = changes.find((c) => c.path === 'visible')
  const lifecycle = lifecycleOf(changes)
  // The `deleted` flag is stated by the banner; repeating it as a row adds nothing.
  const metadata = changes.filter((change) => !isHiddenMeta(change) && !(lifecycle && change.path === 'deleted'))
  const newVersion = changes.find((change) => change.path === 'version')?.after

  return (
    <div className="flex flex-col gap-3">
      {lifecycle && <LifecycleBanner lifecycle={lifecycle} name={audit.entityName} />}

      {snapshot && (
        <Section title="Boundary" defaultOpen>
          <GeofenceDiffMap before={snapshot.before} after={snapshot.after} />
        </Section>
      )}

      {/* No snapshot, so no outline. Map whichever vertices can be placed — as loose markers, never
          joined into a shape — and list all of them: an edit that moved a corner due east changes
          one number, which is not a map but is still the answer to "what changed?". */}
      {!snapshot && boundary.changed && boundary.vertices.length > 0 && (
        <Section title="Boundary" defaultOpen>
          <div className="flex flex-col gap-2">
            {boundary.moved.length > 0 && <GeofenceDiffMap before={[]} after={[]} moved={boundary.moved} />}

            <p className="text-xs text-base-content/60">
              <span className="font-medium text-base-content">{movedSummary(boundary)}</span>{' '}
              {truncated
                ? 'The outline was too large to keep with this record, so it can’t be drawn.'
                : 'This record only keeps the points the edit touched, so the rest of the outline can’t be drawn.'}
            </p>

            {boundary.vertices.map((vertex) => (
              <VertexRow key={vertex.index} vertex={vertex} />
            ))}
          </div>
        </Section>
      )}

      {!snapshot && boundary.changed && boundary.vertices.length === 0 && (
        <p className="rounded-box border border-base-300 bg-base-200 px-3 py-2 text-sm font-medium">
          Boundary points changed
        </p>
      )}

      {(categoryDiff.removed.length > 0 || categoryDiff.added.length > 0) && (
        <Section title="Categories" count={categoryDiff.removed.length + categoryDiff.added.length}>
          <CategoryChanges diff={categoryDiff} />
        </Section>
      )}

      {(visibilityChange ?? metadata.length > 0) && (
        <Section title="Details" count={(visibilityChange ? 1 : 0) + metadata.length}>
          <div className="flex flex-col gap-3">
            {visibilityChange && (
              <VisibilityRow
                state={visibilityChange.state}
                before={visibilityChange.before}
                after={visibilityChange.after}
              />
            )}
            {metadata.map((change) => (
              <ChangeRow key={change.key} change={change} />
            ))}
          </div>
        </Section>
      )}

      {newVersion !== undefined && (
        <p className="text-xs text-base-content/60">
          Version <span className="font-medium text-base-content">{String(newVersion)}</span>
        </p>
      )}
    </div>
  )
}
